import { Outlet, useMatches } from 'react-router';
import { useCanAny } from '../../auth/use-session';
import { AccessDenied } from './access-denied';
import type { AbilityRequirement } from '../../auth/policy';

/**
 * Route-level gating (docs/07-implementation-roadmap.md F1.8, F1.10).
 *
 * The sidebar already hides what a role cannot reach, but a URL can be typed,
 * bookmarked, or pasted by a colleague with a different role — so the route
 * itself asks too, and answers with an explanation rather than a blank page.
 *
 * It reads the ability from the route's `handle` rather than taking a prop, so
 * the gate lives in the route table next to the path it protects and stays there
 * when a later sprint swaps the screen underneath it. A screen cannot lose its
 * gate by being replaced, which is exactly how gates get lost.
 *
 * This is the client agreeing with the server, never widening anything: the
 * guard behind the endpoint refuses the same call regardless. What it buys is a
 * refusal that reads as an answer instead of as a broken screen.
 */

export interface AbilityHandle {
  ability: AbilityRequirement;
}

function hasAbilityHandle(handle: unknown): handle is AbilityHandle {
  return typeof handle === 'object' && handle !== null && 'ability' in handle;
}

export function RequireAbility() {
  const canAny = useCanAny();
  const matches = useMatches();

  // The innermost route that declares one wins, so a nested screen can be
  // narrower than the section it sits in without restating the section's rule.
  const required = [...matches].reverse().find((match) => hasAbilityHandle(match.handle));

  if (required && hasAbilityHandle(required.handle) && !canAny(required.handle.ability)) {
    return <AccessDenied ability={required.handle.ability} />;
  }

  return <Outlet />;
}
